import { create, get, updatePatch } from "./apiService";

export type DposPaymentMethod = "88pay" | "crypto" | "paylink";
export type DposCryptoFlow = "internal_wallet" | "external_wallet";
export type DposSessionStatus =
  | "active"
  | "pending"
  | "processing"
  | "paid"
  | "confirmed"
  | "expired"
  | "cancelled"
  | "failed";

export interface UpdateAcceptedCurrencyRequest {
  assetId: string;
  isAccepted: boolean;
  createWalletIfMissing?: boolean;
}

export interface DposAcceptedCurrencyBusinessWallet {
  exists: boolean;
  address: string | null;
  fireblocksId: string | null;
  network: string | null;
  identifierEmail: string | null;
}

export interface DposCashierModeSession {
  id?: string;
  _id?: string;
  businessId?: string;
  userId?: string;
  status?: "active" | "exited" | "expired" | string;
  expiresAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DposCashierModeStatus {
  hasPin: boolean;
  pinLength: number | null;
  lockedUntil: string | null;
  isLocked: boolean;
  session: DposCashierModeSession | null;
}

export interface SetupDposCashierPinRequest {
  pin: string;
  confirmPin?: string;
}

export interface VerifyDposCashierPinRequest {
  pin: string;
}

export interface ExitDposCashierModeRequest {
  pin?: string;
}

export interface DposAcceptedCurrency {
  assetId: string;
  symbol: string;
  assetName: string;
  image: string | null;
  chain: string | null;
  isAccepted: boolean;
  businessWallet?: DposAcceptedCurrencyBusinessWallet;
}

export interface CreateDposSessionRequest {
  amount: number;
  currency?: string;
  paymentMethod?: DposPaymentMethod;
  cryptoFlow?: DposCryptoFlow;
  cryptoAssetId?: string;
}

export interface ProcessDposSessionPaymentRequest {
  cryptoAssetId?: string;
  amount?: string;
  twoFactorToken?: string;
}

export interface DposSessionDetails {
  id?: string;
  _id?: string;
  amount?: number | string;
  currency?: string;
  currencySymbol?: string;
  status?: DposSessionStatus | string;
  payId?: string;
  qrCode?: string;
  qrPayload?: string;
  qrCodeValue?: string;
  deepLink?: string;
  deeplink?: string;
  appLink?: string;
  payLinkId?: string;
  paymentLink?: string;
  externalWalletQr?: string;
  walletAddress?: string;
  address?: string;
  reference?: string;
  paymentReference?: string;
  merchantReference?: string;
  cryptoFlow?: DposCryptoFlow | string | null;
  cryptoAmount?: number | string;
  cryptoCurrency?: string;
  cryptoAsset?: string;
  cryptoAssetId?: string;
  assetSymbol?: string;
  network?: string;
  chainName?: string;
  qrCodeExpiresAt?: string;
  expiresAt?: string;
  paidAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  paymentMethod?: DposPaymentMethod | string;
  paymentOption?: string;
  flowType?: string;
  errorMessage?: string;
  metadata?: Record<string, unknown>;
}

export interface PublicDposSessionDetails {
  id: string;
  businessId: string;
  business?: {
    businessId?: string;
    businessName?: string | null;
    businessLogo?: string | null;
  };
  amount?: number | string;
  currency?: string | null;
  currencySymbol?: string;
  reference?: string | null;
  payId?: string | null;
  paymentMethod?: DposPaymentMethod | string;
  cryptoFlow?: DposCryptoFlow | string | null;
  cryptoAsset?: string | null;
  cryptoAssetId?: string | null;
  cryptoAmount?: number | string | null;
  exchangeRate?: number | string | null;
  chainId?: string | null;
  network?: string | null;
  walletAddress?: string | null;
  externalWalletQr?: string | null;
  appLink?: string | null;
  deepLink?: string | null;
  payLinkId?: string | null;
  paymentLink?: string | null;
  qrCodeExpiresAt?: string | null;
  expiresAt?: string | null;
  status?: DposSessionStatus | string;
  paidAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DposSessionReceipt {
  paymentId: string;
  status: DposSessionStatus | string;
  amount: string;
  currency: string;
  paymentMethod: DposPaymentMethod | string;
  cryptoFlow?: DposCryptoFlow | string | null;
  paidWith: string | null;
  assetUsed: string | null;
  fiatEquivalent: string | null;
  reference: string | null;
  networkFee: string | null;
  totalCost: string | null;
  totalCostFiatEquivalent: string | null;
  timestamp: string | null;
}

export interface DposSessionListResponse {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  results: DposSessionDetails[];
}

export interface DposPerformanceResponse {
  labels: string[];
  values: number[];
  totalAmount: number;
  allTimeTotal: number;
  change: number;
  changePercent: number | null;
}

type AcceptedCurrenciesResponse =
  | DposAcceptedCurrency[]
  | { currencies?: DposAcceptedCurrency[] };

interface DposServiceErrorResponse {
  message?: string;
  error?: string;
  data?: unknown;
}

interface DposServiceErrorLike {
  message?: string;
  statusCode?: number;
  response?: DposServiceErrorResponse;
}

export interface DposRequestError extends Error {
  statusCode?: number;
  data?: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getDposServiceErrorMessage(error: unknown, fallback: string): string {
  if (!isRecord(error)) {
    return fallback;
  }

  const serviceError = error as DposServiceErrorLike;
  return (
    serviceError.response?.message ||
    serviceError.response?.error ||
    serviceError.message ||
    fallback
  );
}

function getDposServiceErrorData(
  error: DposServiceErrorLike
): Record<string, unknown> | undefined {
  const response = error.response;

  if (isRecord(response?.data)) {
    return response.data;
  }

  if (isRecord(response)) {
    return response;
  }

  return undefined;
}

function buildDposRequestError(
  error: unknown,
  fallback: string
): DposRequestError {
  const serviceError = isRecord(error) ? (error as DposServiceErrorLike) : {};
  const nextError = new Error(
    getDposServiceErrorMessage(error, fallback)
  ) as DposRequestError;

  nextError.statusCode = serviceError.statusCode;
  nextError.data = getDposServiceErrorData(serviceError);

  return nextError;
}

function normalizeAcceptedCurrenciesResponse(
  response: AcceptedCurrenciesResponse
): DposAcceptedCurrency[] {
  return Array.isArray(response) ? response : response.currencies || [];
}

function shouldIncludeDposParam(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized !== "" && normalized !== "undefined";
  }

  return true;
}

function buildDposQueryParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  return Object.entries(params).reduce<Record<string, unknown>>(
    (queryParams, [key, value]) => {
      if (shouldIncludeDposParam(value)) {
        queryParams[key] = typeof value === "string" ? value.trim() : value;
      }
      return queryParams;
    },
    {}
  );
}

export const getAcceptedCurrencies = async (
  businessId: string
): Promise<DposAcceptedCurrency[]> => {
  const response = await get<AcceptedCurrenciesResponse>(
    `/api/v2/business/${businessId}/wallet/dpos/accepted-currencies`
  );
  return normalizeAcceptedCurrenciesResponse(response);
};

export const updateAcceptedCurrency = async (
  businessId: string,
  data: UpdateAcceptedCurrencyRequest
): Promise<DposAcceptedCurrency[]> => {
  try {
    const response = await updatePatch<AcceptedCurrenciesResponse>(
      `/api/v2/business/${businessId}/wallet/dpos/accepted-currencies`,
      {},
      data
    );
    return normalizeAcceptedCurrenciesResponse(response);
  } catch (error: unknown) {
    throw buildDposRequestError(
      error,
      `Could not update accepted currency: ${getDposServiceErrorMessage(
        error,
        "Unknown error"
      )}`
    );
  }
};

export const createDposSession = async (
  businessId: string,
  data: CreateDposSessionRequest
): Promise<DposSessionDetails> => {
  return create<DposSessionDetails>(
    `/api/v2/business/${businessId}/wallet/dpos/sessions`,
    data
  );
};

export const getDposSessionQr = async (
  businessId: string,
  sessionId: string
): Promise<DposSessionDetails> => {
  return get<DposSessionDetails>(
    `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/qr`
  );
};

export interface ListDposSessionsParams {
  page?: number;
  limit?: number;
  status?: string;
  sort?: string;
  search?: string;
}

export const listDposSessions = async (
  businessId: string,
  params: ListDposSessionsParams = {}
): Promise<DposSessionListResponse> => {
  return get<DposSessionListResponse>(
    `/api/v2/business/${businessId}/wallet/dpos/sessions`,
    buildDposQueryParams(params as Record<string, unknown>)
  );
};

export const getDposSessionReceipt = async (
  businessId: string,
  sessionId: string
): Promise<DposSessionReceipt> => {
  return get<DposSessionReceipt>(
    `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/receipt`
  );
};

export const processDposSessionPayment = async (
  businessId: string,
  sessionId: string,
  data: ProcessDposSessionPaymentRequest = {}
): Promise<DposSessionDetails> => {
  const bid = encodeURIComponent(businessId);
  const sid = encodeURIComponent(sessionId);
  return create<DposSessionDetails>(
    `/api/v2/transactions/dpos/businesses/${bid}/sessions/${sid}/pay`,
    data
  );
};

export const getDposPayerSession = async (
  businessId: string,
  sessionId: string
): Promise<DposSessionDetails> => {
  const bid = encodeURIComponent(businessId);
  const sid = encodeURIComponent(sessionId);
  return get<DposSessionDetails>(
    `/api/v2/transactions/dpos/businesses/${bid}/sessions/${sid}`
  );
};

export const getPublicDposSession = async (
  businessId: string,
  sessionId: string
): Promise<PublicDposSessionDetails> => {
  const bid = encodeURIComponent(businessId);
  const sid = encodeURIComponent(sessionId);
  return get<PublicDposSessionDetails>(
    `/api/v2/transactions/dpos/businesses/${bid}/sessions/${sid}`
  );
};

export const getDposPerformance = async (
  businessId: string,
  period = "1w"
): Promise<DposPerformanceResponse> => {
  return get<DposPerformanceResponse>(
    `/api/v2/business/${businessId}/wallet/dpos/performance`,
    buildDposQueryParams({ period })
  );
};

export const cancelDposSession = async (
  businessId: string,
  sessionId: string
): Promise<DposSessionDetails> => {
  return create<DposSessionDetails>(
    `/api/v2/business/${businessId}/wallet/dpos/sessions/${sessionId}/cancel`,
    {}
  );
};

export const getDposCashierModeStatus = async (
  businessId: string
): Promise<DposCashierModeStatus> => {
  return get<DposCashierModeStatus>(
    `/api/v2/business/${businessId}/wallet/dpos/cashier-mode`
  );
};

export const setupDposCashierPin = async (
  businessId: string,
  data: SetupDposCashierPinRequest
): Promise<DposCashierModeStatus> => {
  return create<DposCashierModeStatus>(
    `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/pin`,
    data
  );
};

export const verifyDposCashierPin = async (
  businessId: string,
  data: VerifyDposCashierPinRequest
): Promise<DposCashierModeStatus> => {
  return create<DposCashierModeStatus>(
    `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/verify`,
    data
  );
};

export const exitDposCashierMode = async (
  businessId: string,
  data: ExitDposCashierModeRequest = {}
): Promise<DposCashierModeStatus> => {
  return create<DposCashierModeStatus>(
    `/api/v2/business/${businessId}/wallet/dpos/cashier-mode/exit`,
    data
  );
};
